import React, { useState, useEffect } from 'react';
import { StudCard } from './StudCard';
import { StudForm } from './StudForm';
import { studService } from '../services/studApi';
import { BREEDS } from '../data/mock';
import { Plus, Crown, Loader2 } from 'lucide-react';

export const StudsSection = ({ isAdmin }) => {
  const [studs, setStuds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBreed, setSelectedBreed] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStud, setEditingStud] = useState(null);

  // Filter breeds - exclude adoption and "otras razas"
  const breedOptions = BREEDS.filter(b => b.id !== 'adopcion' && b.id !== 'otras-razas');

  const loadStuds = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await studService.getAll();
      setStuds(data || []);
    } catch (err) {
      setStuds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStuds();
  }, [loadStuds]);

  const filteredStuds = selectedBreed
    ? studs.filter(s => s.breed === selectedBreed)
    : studs;

  const handleSaveStud = async (studData) => {
    try {
      if (editingStud) {
        const updated = await studService.update(studData.id, studData);
        setStuds(studs.map(s => s.id === studData.id ? updated : s));
      } else {
        const created = await studService.create(studData);
        setStuds([...studs, created]);
      }
      setEditingStud(null);
    } catch (err) {
      void err;
      alert('Error al guardar el semental');
    }
  };

  const handleDeleteStud = async (id) => {
    try {
      await studService.delete(id);
      setStuds(studs.filter(s => s.id !== id));
    } catch (err) {
      void err;
      alert('Error al eliminar el semental');
    }
  };

  const handleEditStud = (stud) => {
    setEditingStud(stud);
    setIsFormOpen(true);
  };

  // Count by breed
  const studsByBreed = breedOptions.map(breed => ({
    ...breed,
    count: studs.filter(s => s.breed === breed.id).length
  })).filter(b => b.count > 0 || selectedBreed === b.id);

  return (
    <section className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-3xl p-8 mb-10 border-2 border-purple-200">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-4 py-2 rounded-full mb-4">
          <Crown className="w-5 h-5" />
          <span className="font-semibold">Nuestros Reproductores</span>
        </div>
        <h2 className="text-3xl font-bold text-purple-900 mb-2">
          Sementales
        </h2>
        <p className="text-purple-700">
          Conoce a nuestros reproductores de élite por raza
        </p>
      </div>

      {/* Admin Add Button */}
      {isAdmin && (
        <div className="mb-6">
          <button
            data-testid="add-stud-btn"
            onClick={() => { setEditingStud(null); setIsFormOpen(true); }}
            className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            Añadir Semental
          </button>
        </div>
      )}

      {/* Breed Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setSelectedBreed(null)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
            selectedBreed === null
              ? 'bg-purple-600 text-white shadow-md'
              : 'bg-white text-purple-700 hover:bg-purple-50 border border-purple-200'
          }`}
        >
          Todos ({studs.length})
        </button>
        {studsByBreed.map((breed) => (
          <button
            key={breed.id}
            onClick={() => setSelectedBreed(breed.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
              selectedBreed === breed.id
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-purple-700 hover:bg-purple-50 border border-purple-200'
            }`}
          >
            <span>{breed.icon}</span>
            <span>{breed.name}</span>
            <span className="ml-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs">
              {breed.count}
            </span>
          </button>
        ))}
      </div>

      {/* Studs Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-10 h-10 animate-spin text-purple-500 mx-auto mb-3" />
          <p className="text-purple-600">Cargando sementales...</p>
        </div>
      ) : filteredStuds.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStuds.map(stud => (
            <StudCard
              key={stud.id}
              stud={stud}
              isAdmin={isAdmin}
              onEdit={handleEditStud}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white/50 rounded-2xl">
          <Crown className="w-12 h-12 mx-auto text-purple-300 mb-3" />
          <h3 className="text-lg font-semibold text-purple-800 mb-1">
            {selectedBreed 
              ? `No hay sementales de ${breedOptions.find(b => b.id === selectedBreed)?.name}`
              : 'No hay sementales todavía'
            }
          </h3>
          {isAdmin && (
            <p className="text-purple-600 text-sm">
              Añade sementales con el botón de arriba
            </p>
          )}
        </div>
      )}

      {/* Stud Form Modal */}
      <StudForm
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingStud(null); }}
        onSave={handleSaveStud}
        onDelete={handleDeleteStud}
        editingStud={editingStud}
      />
    </section>
  );
};
